// ---------- l'avis sur le Play Store ----------
// Une carte de notation, montrée au plus trois fois dans la vie d'une installation, et jamais au
// moment où le joueur vient de finir une feuille.
//
// LA CARTE N'EST PAS À NOUS. `requestReview()` ne fait qu'une chose : demander à Play d'afficher
// SA carte, avec ses étoiles et son champ de commentaire. Rien n'est configurable, et surtout
// rien ne revient : l'API ne dit ni si la carte s'est affichée, ni si le joueur a noté. Play
// applique son propre quota par-dessus le nôtre et peut ne rien montrer du tout, en résolvant la
// promesse exactement comme si tout s'était bien passé.
//
// D'où la seule règle qui compte ici : on compte les TENTATIVES, jamais les succès. Compter
// autre chose reviendrait à redemander indéfiniment à celui à qui Play n'a jamais rien montré —
// c'est-à-dire à harceler précisément la personne qu'on croyait épargner.
//
// Et puisque ni l'apparence ni le résultat ne nous appartiennent, le QUAND est le seul levier
// qui reste. Il est en entier dans canAskForReview().

import { InAppReview } from '@capacitor-community/in-app-review';

import { isNative } from './platform';
import { progressIndex } from './progress';

const KEY = 'codigrid:review:v1';
const DAY = 24 * 60 * 60 * 1000;

/** Trois feuilles : en dessous, le joueur n'a pas vu ce que le jeu devient — un autre alphabet,
 *  la récolte d'un symbole, le rejeu de sa propre main à la fin.
 *
 *  Il y a eu une condition de plus ici — trois JOURS depuis l'installation, « en trois jours,
 *  c'est une habitude » — et elle est partie : la plupart des installations ne reviennent jamais
 *  après le premier jour, donc attendre le calendrier écartait justement les enthousiastes du
 *  premier soir, les seuls notes faciles qu'on aura jamais. La condition « il est REVENU » (une
 *  feuille finie dans une session PASSÉE, voir SESSION_START) garde tout ce que les trois jours
 *  protégeaient vraiment : deux sessions au moins, jamais d'empilement de sollicitations. */
const MIN_SHEETS = 3;
/** Un mois entre deux demandes. C'était un trimestre, et c'était compter double : Play applique
 *  déjà son propre quota par appareil (de l'ordre du mois), donc 120 jours ne protégeaient
 *  personne — ils retiraient juste des tentatives à un quota qu'on ne voit pas. */
const BETWEEN_MS = 30 * DAY;
/** Après trois passages sans suite, le joueur a répondu. Insister, c'est le pousser à noter
 *  pour faire cesser la question — et cette note-là, on ne veut pas la recevoir. */
const MAX_ASKS = 3;
/** La fenêtre où les régressions se découvrent. Demander un avis là, c'est aller chercher
 *  l'avis d'un bug qu'on n'a pas encore corrigé. C'était sept jours, et la cadence réelle de
 *  publication a tranché : entre le 20 août et le 22 septembre 2026, une version tous les deux à
 *  huit jours — à ce rythme, sept jours de silence re-muselaient les joueurs FIDÈLES à chaque
 *  livraison, précisément ceux qui avaient un avis à donner. Trois jours ont suivi, puis un seul
 *  (le 2026-10-09) : les régressions d'affichage se voient à la PREMIÈRE session, et la première
 *  session après une mise à jour tombe presque toujours dans les vingt-quatre heures. Au-delà, on
 *  ne protège plus personne — à une version tous les deux à huit jours, trois jours fermaient
 *  encore l'entonnoir près de la moitié du temps. Un jour couvre la fenêtre du bug frais, et
 *  laisse le temps d'un correctif avant que la carte ne parte. Le prix : des correctifs enchaînés
 *  relancent ce jour à chaque fois — acceptable, c'est justement quand des bugs circulent. */
const AFTER_UPDATE_MS = 1 * DAY;
/** La carte du flux (voir canShowRateCard) : trois feuilles, comme la carte de Play. Elle en
 *  exigeait cinq — « elle occupe l'écran, le joueur doit avoir quelque chose à dire » —, ramenée à
 *  trois le 2026-10-22 : les joueurs qui reviennent sont rares (cf. MIN_SHEETS), et deux feuilles
 *  de plus à attendre en écartaient la plupart. */
const CARD_SHEETS = 3;
/**
 * Après une demande à Play, la carte du flux attend trois jours — et non le mois qui sépare deux
 * demandes à Play.
 *
 * Le mois s'appliquait aux deux, et la carte ne naissait presque jamais (relevé le 2026-10-22) :
 * Play passe d'abord, dès trois feuilles ; sa demande bloquait la carte un mois ; un mois plus tard
 * Play pouvait redemander, donc repassait devant. La carte attendait l'épuisement des trois
 * demandes — trois mois au moins, pour les rares joueurs encore là. Or la carte de Play est
 * INVISIBLE une fois sur deux (son quota, qu'on ne voit pas) : une demande partie ne dit pas que
 * le joueur a été sollicité. Trois jours, c'est un ou deux retours d'écart entre les deux
 * invitations, jamais la même session.
 */
const CARD_AFTER_ASK_MS = 3 * DAY;
/**
 * Une mauvaise note de feuille (1 à 3 étoiles) cesse de faire taire les invitations après autant
 * de feuilles terminées depuis.
 *
 * Elle comptait pour toujours (jusqu'au 2026-10-22) : deux étoiles données une fois, puis plus
 * aucune note, et le joueur n'était plus jamais sollicité, même cinquante feuilles plus loin. Or
 * les étoiles jugent UNE IMAGE, pas le jeu, et quelqu'un qui continue de jouer dit avec ses
 * feuilles ce que sa note ne disait pas. Une bonne note posée entre-temps la remplace de toute
 * façon (markSheetRated) ; celle-ci couvre le joueur qui ne note plus.
 */
const FORGIVE_SHEETS = 3;
/** Une fois apparue, la carte du flux reste une semaine (ou jusqu'à sa croix) : une seule
 *  apparition serait un battement d'œil — vue en coin, partie au retour suivant — alors que la
 *  carte n'a qu'UNE occasion de vivre dans l'installation. Une semaine, c'est quelques retours
 *  pour agir, pas une bannière permanente. */
const CARD_LINGER_MS = 7 * DAY;

interface ReviewState {
  /** Première ouverture connue de l'application. */
  first: number;
  /** Nombre de demandes TENTÉES. Voir l'en-tête : jamais des succès. */
  asks: number;
  /** Instant de la dernière tentative. */
  last: number;
  /** Instant où une partie a été terminée, quelle qu'elle soit. Comparé au démarrage de la
   *  session courante, il dit une chose et une seule : le joueur est-il REVENU depuis. */
  finishedAt: number;
  /** La version vue au dernier lancement, pour repérer une mise à jour. */
  version: string;
  /** Quand la version courante est apparue. Zéro tant qu'aucune mise à jour n'a été vue : une
   *  installation neuve n'est pas une mise à jour, et il n'y a aucune raison de lui appliquer
   *  le silence post-livraison. */
  versionAt: number;
  /** Dernière note posée sur une feuille (les étoiles de la carte de fin). Zéro : il ne note
   *  jamais. La note elle-même et non un horodatage : la seule question qu'on lui pose est
   *  « le dernier mot du joueur était-il bon » — voir canAskForReview(). */
  stars: number;
  /** Feuilles terminées au moment de cette note (cf. FORGIVE_SHEETS). -1 : inconnu, pour une
   *  note posée avant que le compte n'existe — il démarre alors au premier passage. */
  starsSheets: number;
  /** Première apparition de la carte d'avis du flux. Zéro : jamais montrée. Comme asks, on
   *  compte l'apparition, jamais la suite — voir l'en-tête. */
  cardAt: number;
  /** La carte a été fermée (croix) ou utilisée (bouton). Elle ne reviendra jamais. */
  cardOff: boolean;
  /** Le texte de la carte montré en dernier (cf. rateCardVariant), -1 : aucun encore. */
  cardText: number;
  /** La session (son instant de départ) où ce texte a été choisi. */
  cardTextSession: number;
}

/**
 * L'instant où la session courante a démarré.
 *
 * C'est le pivot de tout le module. « Le joueur est revenu » ne se mesure pas autrement : une
 * partie terminée AVANT ce point appartient à une session passée. Terminée après, il est encore
 * dans la même session — celle où la carte de fin, les étoiles du puzzle, le partage et
 * l'interstitielle se sont déjà succédé, et où une demande d'avis serait la septième
 * sollicitation d'affilée.
 *
 * Longtemps figé à l'import, donc « session » voulait dire « vie du processus » — et sur Android
 * un processus survit très bien à une soirée en arrière-plan : le joueur qui finissait une
 * feuille, rentrait chez lui et rouvrait le jeu trois heures plus tard n'était jamais « revenu ».
 * La frontière est maintenant la règle de sessionnisation classique : une demi-heure hors de
 * l'écran clôt la session, le retour en ouvre une autre (voir initReview, qui pose l'écouteur).
 */
let sessionStart = Date.now();
/** Trente minutes : le seuil des outils d'analytique, et surtout la différence entre « j'ai
 *  répondu à un message » (même session) et « je reviens jouer » (une autre). */
const SESSION_GAP_MS = 30 * 60 * 1000;
let hiddenAt = 0;
let wired = false;

function blank(): ReviewState {
  return {
    first: Date.now(),
    asks: 0,
    last: 0,
    finishedAt: 0,
    version: '',
    versionAt: 0,
    stars: 0,
    starsSheets: -1,
    cardAt: 0,
    cardOff: false,
    cardText: -1,
    cardTextSession: 0,
  };
}

/** Gardé comme les autres accès au stockage : un quota plein ou un mode privé coûte le suivi
 *  des demandes, jamais la session. */
function read(): ReviewState {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Partial<ReviewState> | null;
    if (!raw || typeof raw !== 'object') return blank();
    const base = blank();
    return {
      first: typeof raw.first === 'number' ? raw.first : base.first,
      asks: typeof raw.asks === 'number' ? raw.asks : 0,
      last: typeof raw.last === 'number' ? raw.last : 0,
      finishedAt: typeof raw.finishedAt === 'number' ? raw.finishedAt : 0,
      version: typeof raw.version === 'string' ? raw.version : '',
      versionAt: typeof raw.versionAt === 'number' ? raw.versionAt : 0,
      stars: typeof raw.stars === 'number' ? raw.stars : 0,
      starsSheets: typeof raw.starsSheets === 'number' ? raw.starsSheets : -1,
      cardAt: typeof raw.cardAt === 'number' ? raw.cardAt : 0,
      cardOff: raw.cardOff === true,
      cardText: typeof raw.cardText === 'number' ? raw.cardText : -1,
      cardTextSession: typeof raw.cardTextSession === 'number' ? raw.cardTextSession : 0,
    };
  } catch {
    return blank();
  }
}

function write(state: ReviewState): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* le jeu continue, sans mémoire des demandes */
  }
}

/**
 * À appeler une fois au démarrage. Pose la date de première ouverture et repère les mises à
 * jour — deux choses qu'il faut avoir enregistrées bien avant la première question.
 */
export function initReview(): void {
  // La frontière de session. Sur `document` et non sur les événements Capacitor : le même code
  // sert l'APK et le web, et visibilitychange couvre l'arrière-plan comme l'onglet masqué. Gardé
  // par `typeof document` pour les tests sous Node — même raison que pickLang (i18n/index.ts).
  if (!wired && typeof document !== 'undefined') {
    wired = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') hiddenAt = Date.now();
      else if (hiddenAt && Date.now() - hiddenAt >= SESSION_GAP_MS) sessionStart = Date.now();
    });
  }
  const state = read();
  // Une version différente de celle du dernier lancement est une mise à jour. Sur un état neuf
  // (`version` vide) c'est une installation : on note la version sans armer le silence.
  if (state.version !== __VERSION__) {
    state.versionAt = state.version === '' ? 0 : Date.now();
    state.version = __VERSION__;
  }
  write(state);
}

/** Première ouverture connue de l'application. Lue aussi par la pub de pause (src/adPacing.ts),
 *  qui épargne les premiers jours : c'est la seule date d'installation que le jeu tienne — en
 *  poser une seconde maintenant offrirait deux jours de grâce à des joueurs installés depuis des
 *  semaines. */
export function installedAt(): number {
  return read().first;
}

/** Une feuille vient d'être terminée. Le seul écrivain de `finishedAt`. */
export function markGameFinished(): void {
  const state = read();
  state.finishedAt = Date.now();
  write(state);
}

/**
 * Le joueur vient de noter une feuille (les étoiles de la carte de fin, ui.ts). C'est le signal
 * de ciblage de canAskForReview() : les étoiles existent pour l'Atelier, pas pour nous, mais
 * elles disent gratuitement qui est content — et surtout qui ne l'est pas. Zéro (la note
 * reprise) n'est pas une note : on ignore, l'avis précédent reste le dernier mot connu.
 */
export function markSheetRated(stars: number): void {
  if (stars <= 0) return;
  const state = read();
  state.stars = stars;
  state.starsSheets = finishedSheets();
  write(state);
}

/** Combien de feuilles ce joueur a menées au bout. Lu dans les sauvegardes plutôt que compté à
 *  part : le nombre est déjà là, et un compteur parallèle ne pourrait que diverger de lui. */
function finishedSheets(): number {
  let n = 0;
  for (const p of progressIndex().values()) if (p.done) n++;
  return n;
}

/**
 * La dernière note de feuille fait-elle encore taire les invitations ? Vrai seulement si elle est
 * mauvaise (1 à 3 étoiles) ET que le joueur n'a pas terminé FORGIVE_SHEETS feuilles depuis.
 *
 * Une note d'avant le compte (starsSheets à -1, une installation mise à jour) le démarre ici,
 * maintenant : on ne sait pas quand elle a été posée, et la compter comme ancienne lèverait d'un
 * coup le silence de tous ceux qui en ont donné une mauvaise. C'est la seule écriture d'une
 * fonction de lecture, et elle n'a lieu qu'une fois par installation.
 */
function badNoteHolds(state: ReviewState, sheets: () => number): boolean {
  if (!(state.stars > 0 && state.stars < 4)) return false;
  if (state.starsSheets < 0) {
    state.starsSheets = sheets();
    write(state);
  }
  return sheets() < state.starsSheets + FORGIVE_SHEETS;
}

/**
 * Toutes les conditions, et elles sont toutes nécessaires. Exportée pour être lisible d'un seul
 * tenant — et parce que c'est le seul endroit du module qui mérite d'être discuté.
 */
export function canAskForReview(): boolean {
  return canAskCore(finishedSheets);
}

/**
 * Le corps de canAskForReview, avec le compte de feuilles INJECTÉ : canShowRateCard() a besoin
 * du même compte pour son propre seuil, et sans ce partage chaque repeinture du catalogue
 * payait deux balayages complets des sauvegardes — voir le dernier commentaire de la fonction.
 */
function canAskCore(sheets: () => number): boolean {
  if (!isNative()) return false; // la carte de Play n'existe que dans l'APK

  const state = read();
  const now = Date.now();

  // Les plafonds d'abord : les moins chers à évaluer, et les plus définitifs. `last` est
  // l'espaceur COMMUN aux deux invitations — la naissance de la carte du flux l'écrit aussi
  // (markRateCardShown) : une invitation à noter par mois, toutes mécaniques confondues.
  if (state.asks >= MAX_ASKS) return false;
  if (state.last && now - state.last < BETWEEN_MS) return false;

  // Puis la mise à jour récente, qui ne coûte pas plus cher et disqualifie tout aussi sec.
  if (state.versionAt && now - state.versionAt < AFTER_UPDATE_MS) return false;

  // Le ciblage : s'il note ses feuilles, sa DERNIÈRE note doit être bonne. Celui dont le
  // dernier mot est 1 à 3 étoiles n'est pas sollicité — c'est l'avis qu'on ne veut pas
  // provoquer —, le temps de quelques feuilles (cf. FORGIVE_SHEETS) ; celui qui ne note jamais
  // (stars à 0) garde le comportement d'avant : on ne sait rien de lui, on tente.
  if (badNoteHolds(state, sheets)) return false;

  // Il EST REVENU : la condition qui porte tout le reste. Voir sessionStart.
  if (!(state.finishedAt > 0 && state.finishedAt < sessionStart)) return false;

  // Et il a assez joué pour avoir un avis. EN DERNIER, délibérément : c'est la seule condition
  // qui coûte — elle relit toutes les sauvegardes — et depuis la carte du flux elle est
  // réévaluée à chaque repeinture du catalogue, qui doit rester instantanée (cf. paint()).
  return sheets() >= MIN_SHEETS;
}

// ---------- la carte d'avis du flux ----------
// L'autre chemin vers la fiche, et son contraire exact : la carte de Play est invisible une fois
// sur deux (son quota) mais ne coûte qu'un tap ; celle-ci s'affiche à coup sûr — c'est notre DOM
// — mais envoie vers le Store. D'où ses règles propres : une seule occasion dans la vie de
// l'installation, et jamais à proximité d'une carte de Play. Les deux partagent l'espaceur `last`,
// mais pas avec le même délai : la carte attend trois jours après une demande à Play
// (CARD_AFTER_ASK_MS), Play attend un mois après la naissance de la carte (BETWEEN_MS).

/**
 * La carte a-t-elle sa place dans le flux en ce moment ? Pure lecture : c'est home.ts qui peint,
 * et qui déclare l'apparition via markRateCardShown() quand il la pose pour de bon.
 */
export function canShowRateCard(): boolean {
  if (!isNative()) return false; // elle invite à noter sur Play : hors de l'APK, pas de sens

  const state = read();
  if (state.cardOff) return false;

  // Déjà apparue : elle vit sa semaine, puis s'éteint pour toujours. Une apparition unique
  // serait un battement d'œil ; une carte permanente, du mobilier qu'on ne voit plus.
  if (state.cardAt) return Date.now() - state.cardAt <= CARD_LINGER_MS;

  // Pas encore née. Jamais dans les trois jours qui suivent une demande à Play (cf.
  // CARD_AFTER_ASK_MS) : sans ce garde, la carte naissait au tap d'onglet suivant la demande —
  // l'espacement rendait canAskForReview faux à l'instant même où la demande partait, ce qui
  // ouvrait la porte qu'il fermait. Avant la naissance, `last` ne vient que de Play.
  if (state.last && Date.now() - state.last < CARD_AFTER_ASK_MS) return false;

  // Le balayage des sauvegardes au plus une fois, partagé avec canAskCore : paresseux parce
  // que canAskCore peut répondre sans lui, et mémorisé parce qu'on en a besoin plusieurs fois.
  let count = -1;
  const sheets = (): number => (count < 0 ? (count = finishedSheets()) : count);

  // Même ciblage que la carte de Play : une mauvaise dernière note la fait taire un temps.
  if (badNoteHolds(state, sheets)) return false;

  // Et même calme : il est revenu, rien ne vient de lui être demandé.
  if (!(state.finishedAt > 0 && state.finishedAt < sessionStart)) return false;

  // La carte de Play passe d'abord : elle ne coûte qu'un tap, et son quota est le plus précieux
  // des deux. Si elle peut partir à ce retour, la nôtre attend le suivant.
  if (canAskCore(sheets)) return false;

  return sheets() >= CARD_SHEETS;
}

/** La carte est-elle déjà née ? home.ts le demande pour distinguer « ne pas la faire naître à
 *  ce retour-ci » (une pub de pause vient de passer) de « la cacher » : une carte vivante ne
 *  doit pas clignoter au gré des retours. */
export function rateCardStarted(): boolean {
  return read().cardAt > 0;
}

/** La carte vient d'être posée dans le flux. Premier passage seulement : l'horloge de sa
 *  semaine démarre une fois, les repeints suivants ne la remontent pas. */
export function markRateCardShown(): void {
  const state = read();
  if (state.cardAt) return;
  state.cardAt = Date.now();
  // La naissance consomme l'espaceur mensuel commun (voir canAskForReview) : le joueur qui
  // vient de recevoir NOTRE invitation ne doit pas recevoir celle de Play à la session
  // suivante — fermer la carte est une réponse, pas une invitation à reposer la question.
  state.last = state.cardAt;
  write(state);
}

/** Le nombre de textes de la carte du flux : home.rate.1 à home.rate.3 dans les dictionnaires. */
export const RATE_CARD_TEXTS = 3;

/**
 * Le texte de la carte du flux à montrer, de 0 à RATE_CARD_TEXTS - 1.
 *
 * TROIS TEXTES EN ALTERNANCE, un par SESSION (2026-10-22). La carte vit une semaine et revient à
 * chaque retour du joueur ; le même texte à chaque fois devient du mobilier qu'on ne lit plus.
 * Trois angles, chacun sans insister : l'avis qui guide les autres joueurs, l'avis qui prend dix
 * secondes, l'avis qui fait vivre le jeu.
 *
 * Jamais au sein d'une session : le flux se repeint à chaque tap d'onglet (home.ts::paint), et un
 * texte qui changerait sous les yeux du joueur aurait l'air d'un bug. Le choix est donc retenu
 * avec l'instant de départ de la session qui l'a fait, et ne bouge qu'à la suivante — la même
 * frontière de trente minutes hors de l'écran que tout le reste de ce module.
 *
 * Mémorisé et non tiré au sort : une suite garantit que deux retours montrent deux textes
 * différents. Aucune mesure ne départage les trois ; si un jour on en a une, c'est ici qu'on
 * choisira.
 */
export function rateCardVariant(): number {
  const state = read();
  if (state.cardText >= 0 && state.cardTextSession === sessionStart) return state.cardText;
  state.cardText = (state.cardText + 1) % RATE_CARD_TEXTS;
  state.cardTextSession = sessionStart;
  write(state);
  return state.cardText;
}

/** Croix ou bouton, même verdict : le joueur a répondu, la carte ne reviendra jamais. */
export function dismissRateCard(): void {
  const state = read();
  state.cardOff = true;
  write(state);
}

/**
 * Demande la carte, si le moment s'y prête. Sans effet ailleurs.
 *
 * L'appelant est openHome(), APRÈS que le catalogue a fini de se charger : une demande d'avis
 * posée sur « Impossible de contacter le serveur » est une demande d'avis sur une panne.
 */
export async function askForReview(): Promise<void> {
  if (!canAskForReview()) return;
  // La tentative est notée AVANT l'appel, et c'est délibéré : si `requestReview()` lève, on ne
  // saura jamais si la carte avait été montrée entre-temps. Une demande perdue vaut mieux
  // qu'une demande répétée.
  const state = read();
  state.asks += 1;
  state.last = Date.now();
  write(state);
  try {
    await InAppReview.requestReview();
  } catch {
    /* pas de Play Services, hors ligne, version installée hors du Store : le jeu continue */
  }
}

/** La fiche du jeu sur le Store. `market://` ouvre l'application Play directement ; le lien web
 *  est le repli, et le seul chemin possible hors de l'APK. */
const PACKAGE = 'fr.tidou44.codigrid';
const STORE_URL = `https://play.google.com/store/apps/details?id=${PACKAGE}`;

/**
 * « Noter Codigrid », depuis les Réglages.
 *
 * Volontairement PAS `requestReview()` : cette carte-là n'est pas faite pour être appelée depuis
 * un bouton bien visible, et surtout Play peut ne rien afficher — un bouton qui ne fait rien une
 * fois sur deux est pire que pas de bouton. Ici le joueur a demandé explicitement, il doit
 * arriver sur la fiche.
 */
export function openStoreListing(): void {
  if (isNative()) {
    // Laisse Android choisir : l'application Play si elle est là, le navigateur sinon.
    window.open(`market://details?id=${PACKAGE}`, '_system');
    return;
  }
  window.open(STORE_URL, '_blank', 'noopener');
}
